import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMessages } from '../../components/Messages';
import { apiFetch, getApiUrl } from '../../config/api';
import PickListViewer from '../../components/PickLists/PickListViewer';
import ReassignTaskModal from '../../components/Tasks/ReassignTaskModal';
import SupervisorApprovalDialog from '../../components/Tasks/SupervisorApprovalDialog';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { formatAppDate } from '../../utils/dateUtils';
import './SupervisorOverview.css';

const SupervisorOverview = forwardRef(({ onNavigateToTab }, ref) => {
  const { t } = useTranslation('supervisor');
  const { showError } = useMessages();
  const { openEntity } = useAppNavigate();
  // Details opened from this dashboard come back here with "← Dashboard"
  const dashboardLabel = t('navigation.tabs.dashboard');
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedPickListId, setSelectedPickListId] = useState(null);
  const [pendingReassignment, setPendingReassignment] = useState([]);
  const [selectedReassignTask, setSelectedReassignTask] = useState(null);
  const [pendingApproval, setPendingApproval] = useState([]);
  const [selectedApprovalTask, setSelectedApprovalTask] = useState(null);

  const fetchPendingReassignment = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/production-tasks/tasks/pending-reassignment'));
      if (response.ok) {
        const data = await response.json();
        setPendingReassignment(data);
      }
    } catch (error) {
      console.error('Error fetching pending reassignment tasks:', error);
    }
  };

  const fetchPendingApproval = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/production-tasks/tasks/pending-approval'));
      if (response.ok) {
        const data = await response.json();
        setPendingApproval(data);
      }
    } catch (error) {
      console.error('Error fetching tasks pending approval:', error);
    }
  };

  const fetchOverview = async () => {
    try {
      setRefreshing(true);
      const response = await apiFetch(getApiUrl('/api/supervision/overview'));
      if (response.ok) {
        const data = await response.json();
        setOverview(data);
      } else {
        showError(t('common:ui.error'), t('supervisorOverview.errors.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching overview:', error);
      showError(t('common:ui.networkError'), t('supervisorOverview.errors.unableToFetch'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchOverview();
    fetchPendingReassignment();
    fetchPendingApproval();
    // Auto-refresh every 5 minutes
    const interval = setInterval(() => {
      fetchOverview();
      fetchPendingReassignment();
      fetchPendingApproval();
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Expose refresh function to parent
  useImperativeHandle(ref, () => ({
    refresh: fetchOverview,
    isRefreshing: refreshing,
    lastUpdated: overview?.generated_at
  }));

  // Alerts carry the record they are about (entity_type/entity_id); open it, else fall back to the tab
  const ALERT_ENTITY_TYPES = { inventory_item: 'inventory-item', task: 'task' };
  const handleAlertClick = (alert) => {
    const type = ALERT_ENTITY_TYPES[alert.entity_type];
    if (type && openEntity(type, alert.entity_id, dashboardLabel)) return;
    if (onNavigateToTab) onNavigateToTab(alert.action_tab || 'production-orders');
  };

  const getRelativeTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return t('supervisorOverview.relative.justNow');
    if (diffMins < 60) return t('supervisorOverview.relative.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('supervisorOverview.relative.hoursAgo', { count: diffHours });
    return t('supervisorOverview.relative.daysAgo', { count: diffDays });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'URGENT': return '#dc3545';
      case 'HIGH': return '#fd7e14';
      case 'NORMAL': return '#007bff';
      case 'LOW': return '#6c757d';
      default: return '#007bff';
    }
  };

  const getAlertSeverityColor = (severity) => {
    switch (severity) {
      case 'HIGH': return '#dc3545';
      case 'MEDIUM': return '#fd7e14';
      case 'LOW': return '#ffc107';
      default: return '#17a2b8';
    }
  };

  if (loading) {
    return (
      <div className="supervisor-overview loading">
        <div className="loading-spinner"></div>
        <p>{t('supervisorOverview.loading')}</p>
      </div>
    );
  }

  if (!overview) {
    return (
      <div className="supervisor-overview error">
        <p>{t('supervisorOverview.errors.unableToLoad')}</p>
        <button onClick={fetchOverview} className="btn-retry">
          {t('common:ui.tryAgain')}
        </button>
      </div>
    );
  }

  return (
    <div className="supervisor-overview">
      <div className="overview-grid">
        {/* Material Requisitions Card */}
        <div className="overview-card requisitions">
          <div className="card-header">
            <h3>📋 {t('dashboard.cards.materialRequisitions')}</h3>
            <div className="summary-badges">
              {overview.requisitions.pending_count > 0 && (
                <span className="badge urgent">{overview.requisitions.pending_count} {t('dashboard.status.pending')}</span>
              )}
              <span className="badge info">{overview.requisitions.today_approvals} {t('dashboard.status.today')}</span>
            </div>
          </div>
          <div className="card-content">
            {overview.requisitions.pending_count === 0 ? (
              <div className="empty-state">
                <p>{t('supervisorOverview.empty.noPendingRequisitions')}</p>
              </div>
            ) : (
              <div className="items-list">
                {overview.requisitions.pending_requisitions.map((req) => (
                  <div
                    key={req.requisition_id}
                    className="item clickable-item"
                    onClick={() => onNavigateToTab && onNavigateToTab('requisition-approvals', { requisitionId: req.requisition_id })}
                  >
                    <div className="item-header">
                      <strong>{req.requisition_number}</strong>
                      <span
                        className="priority-badge"
                        style={{ backgroundColor: getPriorityColor(req.priority) }}
                      >
                        {req.priority}
                      </span>
                    </div>
                    <div className="item-details">
                      <p>{req.po_number} • {req.customer_name || t('supervisorOverview.internal')}</p>
                      <p>{t('supervisorOverview.requestedByItems', { name: req.requested_by_name, count: req.item_count })}</p>
                      <small>{getRelativeTime(req.requested_date)}</small>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>


        {/* Production Status Card */}
        <div className="overview-card production">
          <div className="card-header">
            <h3>🏭 {t('dashboard.cards.productionStatus')}</h3>
            <div className="summary-badges">
              <span className="badge info">{overview.production.active_orders} {t('dashboard.status.active')}</span>
              {overview.production.due_orders > 0 && (
                <span className="badge warning">{overview.production.due_orders} {t('dashboard.status.due')}</span>
              )}
            </div>
          </div>
          <div className="card-content">
            {overview.production.critical_orders.length === 0 ? (
              <div className="empty-state">
                <p>{t('supervisorOverview.empty.noCriticalOrders')}</p>
              </div>
            ) : (
              <div className="items-list">
                {overview.production.critical_orders.map((order) => (
                  <div
                    key={order.production_order_id}
                    className="item clickable-item"
                    onClick={() => openEntity('production-order', order.po_number || order.production_order_id, dashboardLabel)}
                  >
                    <div className="item-header">
                      <strong>{order.po_number}</strong>
                      <span
                        className={`alert-badge ${order.alert_type.toLowerCase()}`}
                      >
                        {order.alert_type.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="item-details">
                      <p>{order.customer_name}</p>
                      <p>{order.active_tasks} {t('dashboard.labels.activeTasks')}</p>
                      {order.planned_due_date && (
                        <small>{t('supervisorOverview.due')}: {formatAppDate(order.planned_due_date)}</small>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Quality Management Card */}
        <div className="overview-card quality">
          <div className="card-header">
            <h3>🔍 {t('dashboard.cards.qualityManagement')}</h3>
            <div className="summary-badges">
              {overview.quality.pending_receipts > 0 && (
                <span className="badge info">{t('supervisorOverview.pendingReceipt', { count: overview.quality.pending_receipts })}</span>
              )}
              {overview.quality.quality_holds > 0 && (
                <span className="badge warning">{t('supervisorOverview.holds', { count: overview.quality.quality_holds })}</span>
              )}
            </div>
          </div>
          <div className="card-content">
            {overview.quality.quality_issues.length === 0 ? (
              <div className="empty-state">
                <p>{t('dashboard.messages.noRecentQualityIssues')}</p>
              </div>
            ) : (
              <div className="items-list">
                {overview.quality.quality_issues.map((issue) => (
                  <div
                    key={issue.receipt_id}
                    className="item clickable-item"
                    onClick={() => onNavigateToTab && onNavigateToTab('goods-receipt', { receiptTab: 'history', receiptId: issue.receipt_id })}
                  >
                    <div className="item-header">
                      <strong>{issue.product_name}</strong>
                      <span className="badge warning">{t('supervisorOverview.qualityIssue')}</span>
                    </div>
                    <div className="item-details">
                      <p>{issue.po_number}</p>
                      {issue.held_quantity > 0 && <p>{t('supervisorOverview.heldUnits', { count: issue.held_quantity })}</p>}
                      {issue.rejected_quantity > 0 && <p>{t('supervisorOverview.rejectedUnits', { count: issue.rejected_quantity })}</p>}
                      <small>{getRelativeTime(issue.receipt_date)}</small>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Pick Lists Card */}
        <div className="overview-card picklists">
          <div className="card-header">
            <h3>📝 {t('dashboard.cards.pickLists')}</h3>
            <div className="summary-badges">
              {overview.pick_lists?.pending_count > 0 && (
                <span className="badge warning">{overview.pick_lists.pending_count} {t('dashboard.status.pending')}</span>
              )}
              <span className="badge info">{overview.pick_lists?.today_completed || 0} {t('dashboard.status.today')}</span>
            </div>
          </div>
          <div className="card-content">
            {overview.pick_lists?.pending_count === 0 ? (
              <div className="empty-state">
                <p>{t('supervisorOverview.empty.noPendingPickLists')}</p>
              </div>
            ) : (
              <div className="items-list">
                {overview.pick_lists?.pending_pick_lists?.map((pickList) => (
                  <div
                    key={pickList.pick_list_id}
                    className="item clickable-item"
                    onClick={() => {
                      setSelectedPickListId(pickList.pick_list_id);
                    }}
                  >
                    <div className="item-header">
                      <strong>{pickList.pick_list_number}</strong>
                      <span
                        className="priority-badge"
                        style={{ backgroundColor: getPriorityColor(pickList.priority) }}
                      >
                        {pickList.priority}
                      </span>
                    </div>
                    <div className="item-details">
                      <p>{pickList.order_number} • {pickList.customer_name || t('common:ui.notAvailable')}</p>
                      <p>{t('supervisorOverview.assignedToItems', { name: pickList.assigned_to_name || t('supervisorOverview.unassigned'), count: pickList.item_count })}</p>
                      <p className="pick-type">{pickList.pick_list_type} • {pickList.status}</p>
                      <small>{getRelativeTime(pickList.created_date)}</small>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* System Alerts Card */}
        <div className="overview-card alerts">
          <div className="card-header">
            <h3>🚨 {t('dashboard.cards.systemAlerts')}</h3>
            <div className="summary-badges">
              {overview.alerts.length > 0 && (
                <span className="badge danger">{t('supervisorOverview.alertsCount', { count: overview.alerts.length })}</span>
              )}
            </div>
          </div>
          <div className="card-content">
            {overview.alerts.length === 0 ? (
              <div className="empty-state">
                <p>{t('dashboard.messages.noActiveAlerts')}</p>
              </div>
            ) : (
              <div className="items-list">
                {overview.alerts.map((alert, index) => (
                  <div
                    key={index}
                    className="item alert-item clickable-item"
                    onClick={() => handleAlertClick(alert)}
                  >
                    <div className="item-header">
                      <strong>{alert.title}</strong>
                      <span
                        className="severity-badge"
                        style={{ backgroundColor: getAlertSeverityColor(alert.severity) }}
                      >
                        {alert.severity}
                      </span>
                    </div>
                    <div className="item-details">
                      <p>{alert.message}</p>
                      <small>{getRelativeTime(alert.timestamp)}</small>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Tasks Pending Quantity Approval Card */}
        <div className="overview-card approval">
          <div className="card-header">
            <h3>✅ {t('dashboard.cards.pendingApproval')}</h3>
            <div className="summary-badges">
              {pendingApproval.length > 0 && (
                <span className="badge warning">{pendingApproval.length} {t('dashboard.status.pending')}</span>
              )}
            </div>
          </div>
          <div className="card-content">
            {pendingApproval.length === 0 ? (
              <div className="empty-state">
                <p>{t('dashboard.messages.noTasksPendingApproval')}</p>
              </div>
            ) : (
              <div className="items-list">
                {pendingApproval.map((task) => (
                  <div
                    key={task.task_id}
                    className="item clickable-item"
                    onClick={() => setSelectedApprovalTask(task)}
                  >
                    <div className="item-header">
                      <strong>{task.task_code}</strong>
                      <span className="badge warning">{t('dashboard.labels.approveQuantity')}</span>
                    </div>
                    <div className="item-details">
                      <p>{task.task_name}</p>
                      {task.po_number && <p>{t('supervisorOverview.po')}: {task.po_number}</p>}
                      {task.username && <p>{`${task.first_name || ''} ${task.last_name || ''}`.trim() || task.username}</p>}
                      {task.completed_at && <small>{getRelativeTime(task.completed_at)}</small>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Tasks Awaiting Reassignment Card */}
        <div className="overview-card reassignment">
          <div className="card-header">
            <h3>🔄 {t('supervisorOverview.awaitingReassignment')}</h3>
            <div className="summary-badges">
              {pendingReassignment.length > 0 && (
                <span className="badge warning">{t('supervisorOverview.pausedCount', { count: pendingReassignment.length })}</span>
              )}
            </div>
          </div>
          <div className="card-content">
            {pendingReassignment.length === 0 ? (
              <div className="empty-state">
                <p>{t('supervisorOverview.empty.noTasksAwaitingReassignment')}</p>
              </div>
            ) : (
              <div className="items-list">
                {pendingReassignment.map((task) => (
                  <div
                    key={task.task_id}
                    className="item clickable-item"
                    onClick={() => setSelectedReassignTask(task)}
                  >
                    <div className="item-header">
                      <strong>{task.task_code}</strong>
                      <span className="badge warning">{t('supervisorOverview.paused')}</span>
                    </div>
                    <div className="item-details">
                      <p>{task.task_name}</p>
                      {task.po_number && <p>{t('supervisorOverview.po')}: {task.po_number}</p>}
                      {task.last_operator_name && (
                        <p>{t('supervisorOverview.lastShift', { name: task.last_operator_name.trim() || task.last_operator_username, quantity: task.last_shift_quantity, uom: task.last_shift_uom })}</p>
                      )}
                      {task.handoff_at && (
                        <small>{getRelativeTime(task.handoff_at)}</small>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Pick List Viewer Modal */}
      {selectedPickListId && (
        <PickListViewer
          pickListId={selectedPickListId}
          onClose={() => {
            setSelectedPickListId(null);
            fetchOverview();
          }}
        />
      )}

      {/* Approve Task Quantity Dialog */}
      <SupervisorApprovalDialog
        isOpen={!!selectedApprovalTask}
        task={selectedApprovalTask}
        onClose={() => setSelectedApprovalTask(null)}
        onApprovalComplete={fetchPendingApproval}
      />

      {/* Reassign Task Modal */}
      <ReassignTaskModal
        isOpen={!!selectedReassignTask}
        task={selectedReassignTask}
        onClose={() => setSelectedReassignTask(null)}
        onReassignComplete={() => {
          setSelectedReassignTask(null);
          fetchPendingReassignment();
        }}
      />
    </div>
  );
});

export default SupervisorOverview;